import { ReactSlickCarouselType } from "@new-components/atoms/SlickCarousel";
import { useMemo, useRef, useState } from "react";

/**
 * Custom hook for managing state and controls of a Slick carousel.
 * @param totalItem - Total number of items in the carousel.
 * @param itemPerSlide - Number of items displayed per slide.
 * @param slidesPerView - Number of slides visible at once (default: 1).
 * @returns Object containing slider reference, state values, and control functions.
 */
const useSlickState = (
  totalItem: number,
  itemPerSlide: number,
  slidesPerView = 1
) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef<ReactSlickCarouselType>(null);

  /**
   * Memoized object indicating whether prev/next controls should be enabled.
   */
  const isEnabled = useMemo(() => ({
    prev: currentSlide === 0,
    next: currentSlide >= Math.ceil(totalItem / itemPerSlide) - slidesPerView,
  }), [currentSlide, totalItem, itemPerSlide, slidesPerView]);

  const handleNext = () => {
    if (sliderRef.current && sliderRef.current.slickNext) {
      sliderRef.current.slickNext();
    }
  };
  const handlePrev = () => {
    if (sliderRef.current && sliderRef.current.slickPrev) {
      sliderRef.current.slickPrev();
    }
  };

  /**
   * Updates the current slide state before the slide change occurs.
   * @param currentSlide - Index of the current slide.
   * @param nextSlide - Index of the next slide.
   */
  const handleBeforeChange = (currentSlide: number, nextSlide: number) => {
    setCurrentSlide(nextSlide);
  };

  return {
    sliderRef,
    values: {
      currentSlide,
      isEnabled,
    },
    mutations: {
      handleNext,
      handlePrev,
      handleBeforeChange,
    },
  };
};

export default useSlickState;